/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react'
import { Accordion, AccordionSection } from '@mdsol/lego/lib/Accordion'
import { Button, FormGroup, Form, FormCheck, Popover, OverlayTrigger } from 'react-bootstrap'
import { Formik, Field } from 'formik'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import FsiPropertiesTable from '~/components/doc-preview/properties/FsiPropertiesTable'
import Utils from '~/lib/Utils'
import checkIsPropertyValueMissing from './FsiPropertyUtils'
import FsiPropertiesConfirmScreenModal from './FsiPropertiesConfirmScreenModal'
import TaskApproveModal from '~/components/tasks/TaskApproveModal'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import * as underscore from 'underscore'
import {
  getDetails,
  getDetailsForProperties,
  getDetailsForPropertiesMultipleFiles,
  evalInherited,
  modifyFileMetadatas
} from '~/services/api/files'
import { getAll, getArtifactValues } from '~/services/api/metadata'
import { usersApiService } from '~/services/api/users'
import { getLinkedEntities } from '~/services/api/etmf'
import { toast } from '~/services/toast'
import Loader from '~/components/shared/Loader'

interface FsiPropertiesModalProps {
  fileId?: number | string
  fileIdArray?: any[]
  task?: any
  fileDetails?: any
  fileProperties?: any
  parentFolderId?: number
  isModal?: boolean
  isMDF?: boolean
  MDFType?: string
  selectedWholeResult?: any
  doCloseModal?: (callback?: () => void) => void
  onClose?: () => void
  fsiDetails?: any
  options?: any
  onPropertiesSaved?: () => void // NEW: called after successful save
}

const FsiPropertiesModal: React.FC<FsiPropertiesModalProps> = ({
  fileId: initialFileId,
  fileIdArray,
  task,
  fileDetails,
  fileProperties,
  parentFolderId,
  isModal,
  isMDF,
  MDFType,
  selectedWholeResult,
  doCloseModal,
  onClose,
  fsiDetails,
  options,
  onPropertiesSaved
}) => {
  const fileParams = {
    parentFolderId: parentFolderId,
    selectedWholeResult: selectedWholeResult
  }
  const context = useServiceContext()
  const { constant, service, api, $rootScope } = context
  const { t: translate } = useTranslation()
  const [propertiesDetails, setPropertiesDetails] = useState<any>({})
  const [propertiesInfo, setPropertiesInfo] = useState<any>({
    noProperties: true,
    isInCR: false,
    isTemplate: false,
    isMultiFile: options?.selectedIds?.length > 1,
    containsMultiUser: false,
    containsMultiGroup: false,
    containsMixedValues: false,
    isMDF,
    MDFType
  })
  const eventSubscriptions = useRef<any>(null)
  const [referenceFileData, setReferenceFileData] = useState<any>(false)
  const [assembledMetadata, setAssembledMetadata] = useState<any>(false)
  const [assembledMetadataCopy, setAssembledMetadataCopy] = useState<any>(false)
  const [applyRecursively, setApplyRecursively] = useState(false)
  const [loadingPromise, setLoadingPromise] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  const [userOptions, setUserOptions] = useState<any>(null)
  const [groupOptions, setGroupOptions] = useState<any>(null)
  const [groupOptionsCaRolesDisabled, setGroupOptionsCaRolesDisabled] = useState<any>(null)
  const metadataTemplates = useRef<any>([])
  const users = useRef<any>(null)
  const groups = useRef<any>(null)
  const propertiesAclsIds = useRef<any>([])
  const propertiesPlatformIds = useRef<any>([])
  const [hasChanges, setHasChanges] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<any>({
    requiredPropsAreMissing: false
  })
  const [isOpen, setIsOpen] = useState(true)
  const [isPropertiesActive, setIsPropertiesActive] = useState(false)
  const [isAccessAttributesActive, setIsAccessAttributesActive] = useState(true)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showTaskApproveModal, setShowTaskApproveModal] = useState(false)

  // Ensure data is loaded on mount and when key props change (matches legacy behavior)
  useEffect(() => {
    loadData(true)

    const eventSubs: any[] = []
    if (!isModal) {
      eventSubs.push(
        $rootScope.$on('$stateChangeStart', (event: any, data: any) => {
          const toState = data.toState
          const toParams = data.toParams
          if (hasChanges) {
            event.preventDefault()
            showCloseWarning({ toStateName: toState, toStateParams: toParams })
          }
        })
      )
    }

    eventSubs.push($rootScope.$on('fileupdate:properties', reloadData))
    eventSubs.push($rootScope.$on('fileupdate:details', reloadData))
    eventSubs.push(
      $rootScope.$on('editable2:closeAll', function () {
        for (const key in assembledMetadata) {
          assembledMetadata[key].inEditableM = false
        }
      })
    )

    eventSubscriptions.current = eventSubs
    // Clean up listeners on unmount
    return () => {
      eventSubs.forEach((lsnr) => lsnr())
    }
  }, [initialFileId, fsiDetails, options])

  function isPropertyRequired(property: any) {
    return (
      property.requiredOnFSITemplate &&
      property.checkRequired &&
      ((property.mixedValue && property.mixedValueRequiredEmpty) || !property.mixedValue)
    )
  }

  useEffect(() => {
    let isChanged = false

    const error = {
      requiredPropsAreMissing: false
    }

    if (Array.isArray(assembledMetadata)) {
      assembledMetadata.forEach((property: any) => {
        if (isPropertyRequired(property) && checkIsPropertyValueMissing(property, constant.metadata.type)) {
          error.requiredPropsAreMissing = true
        }
        if (property.isChanged) {
          isChanged = true
        }
      })
    }
    setErrors(error)
    setHasChanges(!!isChanged)
  }, [assembledMetadata])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const toggleProperties = () => {
    setIsPropertiesActive(isPropertiesActive ? false : true)
  }

  const toggleAccessAttributes = () => {
    setIsAccessAttributesActive(isAccessAttributesActive ? false : true)
  }

  function loadData(isInit: boolean) {
    // Matches legacy: always load users/groups, then properties, then templates, then assemble
    setIsSubmitted(false)
    setIsLoading(true)

    // Step 1: Load users/groups
    usersApiService
      .getAssembledUsersGroups()
      .then((resp: any) => {
        users.current = _.clone(resp?.usersAll || [])
        groups.current = _.clone(resp?.groups || [])

        setUserOptions(Utils.transformToSelectOptions(users.current, 'id', 'name', 'name', true))
        setGroupOptions(Utils.transformToSelectOptions(groups.current, 'id', 'name', 'name'))
        setGroupOptionsCaRolesDisabled(
          Utils.transformToSelectOptions(groups.current, 'id', 'name', 'name', true).map((option: any) => {
            const isCAGroupDisabled = option.type === constant.group.type.CLOUDADMIN_ACL
            return {
              label: option.label,
              value: option.value,
              isDisabled: isCAGroupDisabled,
              isFixed: isCAGroupDisabled
            }
          })
        )
        // Step 2: Load properties (multi/single)
        let fileId = _.isNumber(initialFileId) ? initialFileId : fsiDetails ? fsiDetails.id : null
        let fileDetailsPromise: Promise<any>
        if (options?.selectedIds?.length > 1 || selectedWholeResult) {
          fileDetailsPromise = getDetailsForPropertiesMultipleFiles(options?.selectedIds, fileParams)
        } else {
          fileDetailsPromise = isInit && fsiDetails ? Promise.resolve(fsiDetails) : getDetails(fileId)
        }
        return fileDetailsPromise.then((fileData: any) => {
          // If reference, update fileId
          if (fileData.type === constant.file.type.REFERENCE) {
            setReferenceFileData(fileData)
            if (fileData !== undefined) {
              fileId = fileData.symLinkFsiId
            }
          }
          // Step 3: Load property details (if not multi)
          let propsPromise: Promise<any>
          if (options?.selectedIds?.length > 1 || selectedWholeResult) {
            propsPromise = Promise.resolve(fileData)
          } else {
            propsPromise = isInit && fileProperties ? Promise.resolve(fileProperties) : getDetailsForProperties(fileId)
          }
          return propsPromise.then((propsData: any) => {
            // Step 4: Load templates
            const filePropIds = propsData.metadata?.elements?.map((a: any) => a.metadataTemplateId) || []
            return getAll({ id: filePropIds.length ? filePropIds : 0 }).then((resp: any) => {
              metadataTemplates.current = Array.isArray(resp.elements) ? resp.elements : []
              propertiesAclsIds.current = []
              propertiesPlatformIds.current = []
              // Step 5: Assemble metadata (guaranteed order)
              propsPostprocess(propsData, fileData?.type === constant.file.type.REFERENCE).then(() => {
                const allProps = _.isFunction(propsData?.plain) ? propsData.plain() : propsData.plain
                setPropertiesDetails(allProps || propsData)
                setLoadingPromise(null)
                setIsLoading(false)
              })
            })
          })
        })
      })
      .catch((err: any) => {
        // Robust error handling
        setIsLoading(false)
        setPropertiesDetails({})
        setAssembledMetadata([])
        setAssembledMetadataCopy([])
        setErrors({ requiredPropsAreMissing: false })
        // Optionally show error toast
        if (toast && typeof toast.error === 'function') {
          toast.error(translate('METADATA_FSI_SAVING_ERROR', { error: err?.message || 'Unknown error' }))
        }
      })
    setLoadingPromise(null)
  }

  function propsPostprocess(propFileData: any, isAlias?: boolean) {
    return new Promise((resolve) => {
      setPropertiesInfo(
        _.chain(propertiesInfo)
          .clone()
          .extend({
            isInCR: propFileData.systemFolderType === constant.file.systemFolderType.WORKFLOWS,
            isDocument:
              propFileData.type === constant.file.type.DOCUMENT || propFileData.type === constant.file.type.PLACEHOLDER,
            isTemplate: propFileData.systemFolderType === constant.file.systemFolderType.TEMPLATES
          })
          .value()
      )

      const filePropIds = propFileData.metadata.elements.map(function (a: any) {
        return a.metadataTemplateId
      })

      if (isAlias) {
        propFileData.canEditProperties = false
      }

      getAll({ id: filePropIds.length ? filePropIds : 0 }).then(function (resp: any) {
        metadataTemplates.current = Array.isArray(resp.elements) ? resp.elements : []

        propertiesAclsIds.current = []
        propertiesPlatformIds.current = []

        if (isMDF) {
          getLinkedEntities().then(function (data: any) {
            for (const key in data.entityLinks) {
              const section = data.entityLinks[key].section
              const linkedEntityId = data.entityLinks[key].linkedEntityId

              if (section === 'ACCESS_LEVEL') {
                propertiesAclsIds.current.push(linkedEntityId)
              } else if (section === 'PLATFORM') {
                propertiesPlatformIds.current.push(linkedEntityId)
              }
            }
            assembleMetadata(propFileData)
            resolve(propFileData)
          })
        } else {
          assembleMetadata(propFileData)
          resolve(propFileData)
        }
      }, resolve)
    })
  }

  function onRevertItem(property: any) {
    return revertToInherited(property)
  }

  function onCancelItem(property: any) {
    const index = _.indexOf(assembledMetadata, property)
    const am = _.clone(assembledMetadata)
    am.splice(index, 1, _.clone(assembledMetadataCopy[index]))
    setAssembledMetadata(am)
  }

  function onChangeItem(prop: any, value: any, isReturnToInherited?: boolean) {
    const propClone = _.clone(prop)
    _.extend(propClone, {
      revertToInherited: !!isReturnToInherited,
      value: value
    })

    if (propClone.itemCopy && _.isString(propClone.itemCopy.value) && propClone.itemCopy.value === propClone.value) {
      _.extend(propClone, {
        isChanged: false,
        inherited: propClone.itemCopy.inherited
      })
    } else {
      _.extend(propClone, {
        isChanged: true,
        inherited: !!isReturnToInherited
      })
    }

    updateMetadata(prop, propClone)
  }

  function onAcceptItem(prop: any) {
    if (prop.itemCopy && _.isString(prop.itemCopy.value) && prop.itemCopy.value === prop.value) {
      _.extend(prop, {
        isChanged: false,
        revertToInherited: false,
        inherited: prop.itemCopy.inherited
      })
    } else {
      _.extend(prop, {
        isChanged: true,
        revertToInherited: false,
        inherited: false
      })
    }

    updateMetadata(prop)
  }

  function updateMetadata(prop: any, propClone?: any) {
    const am = _.clone(assembledMetadata)
    if (propClone) {
      const index = _.indexOf(assembledMetadata, prop)
      am.splice(index, 1, propClone)
    }
    setAssembledMetadata(am)
  }

  function revertToInherited(item: any) {
    if (item.id !== null) {
      return evalInherited(fsiDetails?.id, item.id).then(
        (data: any) => {
          _.extend(item, {
            value: data.value,
            revertToInherited: true,
            inherited: true,
            isChanged: true
          })

          onChangeItem(item, data.value, true)
        },
        () => {}
      )
    } else {
      _.extend(item, {
        value: _.clone(item.itemCopy.value),
        revertToInherited: true,
        inherited: true,
        isChanged: false
      })
      onCancelItem(item)

      return Promise.resolve()
    }
  }

  function handleTaskApprovalWorkflow() {
    // Show confirmation dialog asking if user wants to approve the task
    const showConfirmationDialog = () => {
      if ((service as any).modal && (service as any).modal.info) {
        ;(service as any).modal.info(
          translate('TASKS_APPROVE_H_TITLE'),
          translate('PROPERTIES_TASK_MODAL_BODY'),
          function (modal: any) {
            // User clicked "Yes" - proceed with task approval
            modal.close()
            setShowTaskApproveModal(true)
          },
          function (modal: any) {
            // User clicked "No" - just show success message
            toast.success(translate('BROWSER_UPLOAD_FILE_SUCCESS_MSG'))
            modal.close()
          },
          true,
          translate('YES'),
          translate('NO')
        )
      } else {
        // Fallback: if legacy modal service unavailable, show success and open approval modal
        toast.success(translate('BROWSER_UPLOAD_FILE_SUCCESS_MSG'))
        setShowTaskApproveModal(true)
      }
    }

    showConfirmationDialog()
  }

  function _updateMetadataFn(modal: any) {
    setIsLoading(true)
    const objToSend = _.reduce(
      assembledMetadata,
      (mem: any[], property: any) => {
        const prop = _.clone(property)
        if (!prop.isChanged && !(applyRecursively && !modal.onlyApplyChangesId)) {
          return mem
        }
        if (
          (!prop.value && prop.template.type !== constant.metadata.type.NUMBER) ||
          prop.value === '' ||
          prop.value === undefined ||
          prop.value === null ||
          prop.value.length === 0
        ) {
          prop.value = null
        }

        if (prop.template.type === constant.metadata.type.DATETIME && prop.value && prop.value.utc) {
          const utcDate = prop.value.utc(true)
          utcDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          prop.value = utcDate.valueOf()
        }
        if (prop.template.type === constant.metadata.type.NUMBER && prop.value) {
          prop.value = +prop.value
        }

        if (applyRecursively) {
          prop.applyRecursively = true
        }

        mem.push(prop)
        return mem
      },
      []
    )

    const refId = options?.selectedIds?.length > 1 ? options?.selectedIds : fsiDetails?.id || initialFileId
    const apiCall = modifyFileMetadatas(refId, objToSend, fileParams)

    apiCall.then(
      function () {
        toast.success(translate('PROPERTIES_UPDATED_SUCCESS_MSG'))

        // Handle task approval workflow
        if (task && task.id !== undefined) {
          closeModal(() => {
            handleTaskApprovalWorkflow()
          })
        } else {
          closeModal()
        }

        $rootScope.$emit('fileupdate:properties', refId)
      },
      (data: any) => {
        toast.error(translate('METADATA_FSI_SAVING_ERROR', { error: data.data.message }))

        if (data.status !== 403) {
          if (modal && _.isFunction(modal.close)) {
            modal.close(true)
          }
        }
        setIsLoading(false)
      }
    )

    return apiCall
  }

  function _propsPostprocess(props: any, isReference: any) {
    console.log('[FsiPropertiesModal] _propsPostprocess called', { props, isReference })
    const propsInfo = _.clone(propertiesInfo)
    propsInfo.noProperties = props.metadata.elements.length === 0
    propsInfo.isInCR = props.systemFolderType === constant.file.systemFolderType.WORKFLOWS

    _(props.metadata.elements).forEach(function (prop: any) {
      assembleOneRowObj(prop, propsInfo)
      prop.itemCopy = _.clone(prop)
    })

    console.log('[FsiPropertiesModal] Setting assembled metadata', { elements: props.metadata.elements, propsInfo })
    setPropertiesInfo(propsInfo)
    setAssembledMetadata(props.metadata.elements)
    setAssembledMetadataCopy(_.clone(props.metadata.elements))
  }

  // Alias for compatibility
  const assembleMetadata = (props: any) => _propsPostprocess(props, false)

  function mapValueToObject(prop: any, value: any) {
    if (prop) {
      switch (prop.template.type) {
        case constant.metadata.type.ARTIFACT:
          return
        case constant.metadata.type.SELECT_ONE_USER:
          return underscore.findWhere(users.current, { id: value })
        case constant.metadata.type.SELECT_ONE_GROUP:
          return underscore.findWhere(groups.current, { id: value })
        case constant.metadata.type.SELECT_MULTI_USER:
          return _.chain(value)
            .map((userId: any) => {
              return underscore.findWhere(users.current, { id: userId })
            })
            .reject((user: any) => {
              return !user
            })
            .value()
        case constant.metadata.type.SELECT_MULTI_GROUP:
          return _.chain(value)
            .map((groupId: any) => {
              return underscore.findWhere(groups.current, { id: groupId })
            })
            .reject((group: any) => {
              return !group
            })
            .value()
      }
    }
    return value
  }

  function assembleOneRowObj(prop: any, propsInfo: any) {
    const template = underscore.findWhere(metadataTemplates.current, {
      id: prop.metadataTemplateId
    })

    // Safety check: if template is not found, return early
    if (!template) {
      console.warn(`Template not found for metadataTemplateId: ${prop.metadataTemplateId}`)
      return
    }

    if (template.definition && template.definition.options) {
      template.definition.reactOptions = Utils.transformToSelectOptions(template.definition.options)
    }

    _.extend(prop, {
      template: template,
      inherited: !prop.id,
      applyRecursively: false,
      revertToInherited: false,
      isChanged: false
    })

    if (prop.mixedValue) {
      propsInfo.containsMixedValues = true
    }
    if (
      _.isArray(propertiesPlatformIds.current) &&
      propertiesPlatformIds.current.indexOf(prop.metadataTemplateId) > -1
    ) {
      prop.isPlatformProperty = true
    }

    switch (prop.template.type) {
      case constant.metadata.type.DATETIME:
        if (prop.value) {
          prop.value = Utils.convertTimestampToUTCMoment(prop.value)
        }
        break
      case constant.metadata.type.SELECT_MULTI_USER:
        propsInfo.containsMultiUser = true
        break
      case constant.metadata.type.SELECT_MULTI_GROUP:
        propsInfo.containsMultiGroup = true
        break
      case constant.metadata.type.ARTIFACT:
        getArtifactValues(prop.metadataTemplateId, fsiDetails?.id || initialFileId).then(function (data: any) {
          prop.template.definition.options = _.map(data.definition.options, (i: any) => {
            return { id: i, name: i }
          })
        })
        break
    }
  }

  function saveChanges(values: any) {
    let didSave = false
    if ((hasChanges || !values?.onlyApplyChangesId) && propertiesDetails?.canEditProperties) {
      _updateMetadataFn(values)
      didSave = true
      if (typeof onPropertiesSaved === 'function') {
        onPropertiesSaved()
      }
    } else if (isModal && propertiesDetails?.canEditProperties) {
      // No changes, but user clicked OK/Save: treat as successful save for workflow
      didSave = true
      if (typeof onPropertiesSaved === 'function') {
        onPropertiesSaved()
      }
    }
    if (isModal) {
      doClose()
    }
  }

  function reloadData() {
    loadData(false)
  }

  function cancelChanges() {
    if (isModal) {
      if (
        hasChanges ||
        (applyRecursively && context.store.formRef?.values && !context.store.formRef.values.onlyApplyChangesId)
      ) {
        setShowConfirmModal(true)
      } else {
        doClose()
      }
    } else {
      reloadData()
    }
  }

  function exit(modal: any, toStateName?: any, toStateParams?: any) {
    underscore.each(eventSubscriptions.current, function (lsnr: any) {
      lsnr()
    })

    if (isModal) {
      // Update internal state before closing via parent
      doClose()
      closeModal()
    } else {
      if (toStateName || toStateParams) {
        // Use legacy redirect if available, otherwise just reload
        if ((service as any).redirect) {
          ;(service as any).redirect(toStateName, toStateParams)
        } else {
          reloadData()
        }
      } else {
        reloadData()
      }
    }
  }

  function closeModal(callback?: () => void) {
    if (doCloseModal) {
      doCloseModal(callback)
    } else {
      if (callback) {
        callback()
      }
    }
  }

  function showCloseWarning(arg?: any) {
    arg = arg || {}

    if (hasChanges) {
      // Use legacy modal if available, otherwise just exit
      if ((service as any).modal && (service as any).modal.info) {
        ;(service as any).modal.info(
          translate('PROPERTIES_LEAVE_SCREEN_TITLE'),
          translate('PROPERTIES_LEAVE_SCREEN_BODY'),
          function (modal: any) {
            modal.close()
          },
          function (modal: any) {
            exit(modal, arg.toStateName, arg.toStateParams)
          },
          true,
          translate('STAY'),
          translate('LEAVE')
        )
      } else {
        exit(null, arg.toStateName, arg.toStateParams)
      }
    } else {
      exit(null, arg.toStateName, arg.toStateParams)
    }
  }

  const popoverReferenceFileProps = (
    <Popover id="multiple-files-popup-info" title={translate('TOOLTIP_TITLE_NOTE')}>
      <div dangerouslySetInnerHTML={{ __html: translate('PROPERTIES_REFERENCE_DOCUMENT_NOTE') }}></div>
    </Popover>
  )

  const forwardedParams = {
    propertiesDetails,
    assembledMetadata,
    assembledMetadataCopy,
    propertiesInfo,
    propertiesAclsIds,
    propertiesPlatformIds,
    userOptions,
    groupOptions,
    groupOptionsCaRolesDisabled
  }

  const forwardedFunctions = {
    onEditItem: () => {},
    onRevertItem,
    onCancelItem,
    onChangeItem,
    onAcceptItem,
    mapValueToObject
  }

  return (
    <>
      <Modal
        show={isOpen}
        title={translate(isMDF ? 'RCM_MASTER_DATA_FILE_PROPERTIES_H_TITLE' : 'BROWSER_PROPERTIES_H_TITLE')}
        handleClose={cancelChanges}
        className="theme-sandman"
      >
        <Formik
          validate={(values) => {
            const errors = {}
            return errors
          }}
          enableReinitialize={true}
          innerRef={(el: any) => {
            context.store.formRef = el
          }}
          initialValues={{ ...fsiDetails, applyRecursively: false, onlyApplyChangesId: true }}
          onSubmit={(values) => {
            saveChanges(values)
          }}
        >
          {({ values, handleSubmit, dirty }) => {
            console.log(
              '[FsiPropertiesModal] Render - isLoading:',
              isLoading,
              'propertiesDetails:',
              propertiesDetails,
              'assembledMetadata:',
              assembledMetadata
            )
            return (
              <div>
                <Modal.Body>
                  {isLoading ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                      <Loader />
                    </div>
                  ) : (
                    <div id="properties-dialog" className="accordion-style">
                      {isModal && (
                        <div>
                          {
                            <div className="properties-label">
                              {propertiesDetails?.type === constant.file.type.FOLDER && (
                                <label className="fs12 mb0">{translate('FOLDER')}</label>
                              )}
                              {propertiesDetails?.type !== constant.file.type.FOLDER && (
                                <label className="fs12 mb0">{translate('DOCUMENT')}</label>
                              )}
                              {!options?.selectedIds && (
                                <p className="whitespaceprewrap fs12">
                                  {referenceFileData?.name || propertiesDetails?.name}
                                </p>
                              )}
                              {options?.selectedIds?.length > 1 && (
                                <p className="fs12">{translate('WORKFLOW_MULTIPLE_DOCS')}</p>
                              )}
                            </div>
                          }

                          {propertiesDetails?.type === constant.file.type.FOLDER && (
                            <div className="properties-label">
                              <label className="fs12 mb0">{translate('AUDITLOG_ENUM_TYPE_FOLDER_TYPE_TPL')}</label>
                              <p className="p-value fs12">{propertiesDetails?.templateName}</p>
                            </div>
                          )}
                          {(propertiesDetails?.type === constant.file.type.DOCUMENT ||
                            propertiesDetails?.type === constant.file.type.PLACEHOLDER) &&
                            !options?.selectedIds && (
                              <div className="properties-label">
                                <label className="fs12 mb0">{translate('DOCUMENT_TYPE')}</label>
                                <p className="p-value fs12">{propertiesDetails?.templateName}</p>
                              </div>
                            )}
                          {(propertiesDetails?.templateId === 231 ||
                            propertiesDetails?.templateId === 226 ||
                            propertiesDetails?.templateId === 227 ||
                            propertiesDetails?.templateId === 230) && (
                            <div className="note-info mb10">{translate('DOCUMENT_PROPERTIES_ETMF_WARNING')}</div>
                          )}
                        </div>
                      )}
                      {isMDF && (
                        <Accordion>
                          <AccordionSection
                            eventKey="properties"
                            headerContent={<span> {translate('PROPERTIES_TAB_H_EDGE_RCM_PROPERTIES')}</span>}
                            collapsed={isPropertiesActive}
                            onSectionToggle={toggleProperties}
                          >
                            <FsiPropertiesTable
                              showJustAcls={false}
                              errors={errors}
                              {...forwardedFunctions}
                              {...forwardedParams}
                            />
                          </AccordionSection>
                          <AccordionSection
                            eventKey="accessAttributes"
                            headerContent={
                              <span> {translate('PROPERTIES_TAB_H_GENERATED_STUDY_STRCT_ACCS_ATTRS')}</span>
                            }
                            collapsed={isAccessAttributesActive}
                            onSectionToggle={toggleAccessAttributes}
                          >
                            <FsiPropertiesTable
                              showJustAcls={true}
                              errors={errors}
                              {...forwardedFunctions}
                              {...forwardedParams}
                            />
                          </AccordionSection>
                        </Accordion>
                      )}
                      {isModal && !isMDF && (
                        <div>
                          <label className="mb0">
                            <span>{translate('PROPERTIES')}</span>
                            {referenceFileData && (
                              <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement="top"
                                overlay={popoverReferenceFileProps}
                              >
                                <span className="info_hover"></span>
                              </OverlayTrigger>
                            )}
                          </label>
                        </div>
                      )}
                      {!loadingPromise && propertiesInfo?.noProperties && !isMDF && (
                        <div>
                          <span className="fs12">
                            {translate('BROWSER_PROPERTIES_EMPTY')}
                            {translate(propertiesDetails?.type === constant.file.type.FOLDER ? 'FOLDER' : 'DOCUMENT')}
                          </span>
                        </div>
                      )}
                      {!isMDF && (
                        <div className={propertiesDetails && referenceFileData ? 'properties-destination-info' : ''}>
                          {propertiesDetails && referenceFileData && (
                            <p className="destination-document-name">
                              <b>{translate('DESTINATION_DOCUMENT')}</b>
                              {!isModal && (
                                <OverlayTrigger
                                  trigger={['hover', 'focus']}
                                  placement="top"
                                  overlay={popoverReferenceFileProps}
                                >
                                  <span className="info_hover"></span>
                                </OverlayTrigger>
                              )}
                            </p>
                          )}

                          <FsiPropertiesTable errors={errors} {...forwardedFunctions} {...forwardedParams} />
                        </div>
                      )}
                      {errors.requiredPropsAreMissing && (
                        <div className="alert alert-danger metadata-form-error form-error">
                          {translate('SOME_REQUIRED_PROPERTIES_ARE_NOT_FILL_OUT')}
                        </div>
                      )}
                      {!loadingPromise &&
                        propertiesDetails?.type === constant.file.type.FOLDER &&
                        propertiesDetails?.canEditProperties &&
                        !propertiesInfo.noProperties && (
                          <div>
                            <Form.Label>{translate('PROPERTIES_LABEL_APPLIES_TO')}</Form.Label>
                            <FormGroup>
                              <FormCheck
                                type="radio"
                                name="oatat3"
                                id="input-oaat3"
                                checked={!applyRecursively}
                                onChange={(event) => {
                                  setApplyRecursively(!applyRecursively)
                                }}
                                inline
                                label={<span>{translate('PROPERTIES_APPLY_TO_THIS_FOLDER_ONLY')}</span>}
                              />
                            </FormGroup>
                            <FormGroup>
                              <FormCheck
                                type="radio"
                                name="oatat3"
                                id="input-ato3"
                                checked={applyRecursively}
                                onChange={(event) => {
                                  setApplyRecursively(!applyRecursively)
                                }}
                                inline
                                label={<span>{translate('PROPERTIES_APPLY_TO_THIS_FOLDER_AND_ALL_ENCLOSED')}</span>}
                              />
                            </FormGroup>
                          </div>
                        )}
                      {propertiesDetails?.type === constant.file.type.FOLDER &&
                        propertiesDetails?.canEditProperties &&
                        !propertiesInfo?.noProperties &&
                        applyRecursively && (
                          <div>
                            <FormGroup>
                              <Field
                                name="onlyApplyChangesId"
                                value={true}
                                checked={values?.onlyApplyChangesId}
                                as={FormCheck}
                                id="e272b37e-809f-4420-9cb7-98cdd903acae"
                                label={<span>{translate('PROPERTIES_ONLY_APPLY_CHANGES')}</span>}
                              />
                            </FormGroup>
                          </div>
                        )}
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  {!isLoading && (
                    <div>
                      {(hasChanges || (applyRecursively && !values?.onlyApplyChangesId)) && (
                        <Button
                          data-action={'cancel'}
                          variant="link"
                          className="text-capitalize"
                          onClick={cancelChanges}
                          type="button"
                        >
                          {translate('CANCEL')}
                        </Button>
                      )}
                      {propertiesDetails?.canEditProperties &&
                        (!isModal || hasChanges || (applyRecursively && !values?.onlyApplyChangesId)) && (
                          <Button
                            data-action={'save'}
                            disabled={errors?.requiredPropsAreMissing || (hasChanges ? !hasChanges : !dirty)}
                            onClick={handleSubmit as any}
                            className="btn btn-primary text-capitalize"
                            type="submit"
                          >
                            {translate('SAVE')}
                          </Button>
                        )}
                      {!hasChanges &&
                        ((applyRecursively && values?.onlyApplyChangesId) || !applyRecursively) &&
                        isModal && (
                          <Button
                            data-action={'ok'}
                            className="btn btn-primary text-capitalize"
                            type="button"
                            onClick={() => {
                              if (typeof onPropertiesSaved === 'function') {
                                onPropertiesSaved()
                              }
                              doClose()
                            }}
                          >
                            {translate('OK')}
                          </Button>
                        )}
                    </div>
                  )}
                </Modal.Footer>
              </div>
            )
          }}
        </Formik>
      </Modal>

      {showConfirmModal && (
        <FsiPropertiesConfirmScreenModal
          closeParentModal={() => {
            setShowConfirmModal(false)
            doClose()
          }}
          onStay={() => {
            setShowConfirmModal(false)
          }}
        />
      )}

      {showTaskApproveModal && task && (
        <TaskApproveModal
          taskId={task.id}
          task={task}
          onClose={() => setShowTaskApproveModal(false)}
          onApprove={() => {
            setShowTaskApproveModal(false)
            toast.success(translate('BROWSER_UPLOAD_FILE_SUCCESS_MSG'))
          }}
        />
      )}
    </>
  )
}

export default FsiPropertiesModal
